"use client";

import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { BsGlobe2 } from "react-icons/bs";
import { IoMdShare } from "react-icons/io";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import SearchCloseButton from "@/components/shared/SearchCloseButton";
import { Autoplay, Mousewheel } from "swiper/modules";

import { useTheme } from "@/components/providers/ThemeProvider";
import NewsMoreOption from "@/components/shared/NewsMoreOption";
import SearchInput from "@/components/shared/SearchInput";
import SocialShare from "@/components/shared/SocialShare";
import { SocialIconsKey, socialIconsMap } from "@/data/social";
import { fetchSearchData } from "@/services/commonService";
import { ArticleListType } from "@/types/article";
import { SocialLinkType } from "@/types/common";
import { ensureHttps } from "@/utils/ensureHttps";
import { getArticleImageUrl, getMediaSrcSet } from "@/utils/getImageUrl";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { getLocalizedString } from "@/utils/getLocalizedString";
import { getCategoryListUrlFromArticle, getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import CustomImage from "../../shared/CustomImage";
import { Typography } from "../../shared/Typography";

import ThemeLogo from "@/components/shared/ThemeLogo";
import {
  HEADER_LOGO_HEIGHT,
  HEADER_LOGO_MODAL_CLASS,
  HEADER_LOGO_WIDTH,
  WEBSITE_NAME,
} from "@/data/constants";
import { getLabel } from "@/data/labels";
import { usePauseAutoplayOnDropdown } from "@/hooks";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/autoplay";

type SearchDataType = {
  keywords: string[];
  news: {
    docs: ArticleListType[];
  };
};

interface SearchModalProps {
  setSearchModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  socialLinks: SocialLinkType[];
}

export default function SearchModal({ setSearchModalOpen, socialLinks }: SearchModalProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const [modalHeight, setModalHeight] = useState("100vh");
  const [currentSlide, setCurrentSlide] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  usePauseAutoplayOnDropdown(swiperRef);

  const { darkMode } = useTheme();
  const { data, error, isLoading } = useQuery<SearchDataType>({
    queryKey: ["fetchSearchData"],
    queryFn: fetchSearchData,
  });

  useEffect(() => {
    const updateHeight = () => {
      const height = `${window.innerHeight}px`;
      setModalHeight(height);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSearchModalOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [setSearchModalOpen]);

  return (
    <div
      className="fixed inset-0 z-[100] w-full min-h-full overflow-x-hidden bg-white dark:bg-darkBgPrimaryColor"
      style={{ height: modalHeight }}
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      <SearchCloseButton
        onClose={() => setSearchModalOpen(false)}
        className="fixed top-4 right-4 z-[110] sm:top-6 sm:right-6"
      />

      <div className="container relative">
        <div className="flex justify-center pt-6 lg:pt-10">
          <Link href="/" onClick={() => setSearchModalOpen(false)} aria-label="Go to home page">
            <ThemeLogo
              className={HEADER_LOGO_MODAL_CLASS}
              alt={WEBSITE_NAME}
              width={HEADER_LOGO_WIDTH}
              height={HEADER_LOGO_HEIGHT}
              decoding="sync"
            />
          </Link>
        </div>

        <div className="flex items-center justify-center gap-3 mt-6">
          {socialLinks?.map((socialLink, index) => {
            const Icon = socialIconsMap[socialLink?.platform as SocialIconsKey] || BsGlobe2;
            return (
              <div key={index} className="flex items-center">
                <p className="w-6 h-6 rounded-md shrink-0 bg-[#585858] text-white dark:bg-white dark:text-black  flex justify-center items-center">
                  <a
                    href={ensureHttps(socialLink?.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow us on ${socialLink?.platform}`}
                  >
                    <Icon />
                  </a>
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-8 lg:mt-10">
          <SearchInput handleSearch={() => setSearchModalOpen(false)} />
        </div>

        <Typography className="mt-8 text-center lg:mt-10" variant="h4">
          {getLabel(locale?.split("-")[0], "MOST_SEARCHED_NEWS")}
        </Typography>

        {/* {!isLoading && (
          <div className="flex flex-wrap items-center justify-center gap-4 mt-6 lg:gap-10">
            {data?.keywords?.map((keyword, index) => {
              return (
                <Link
                  href={`/search?q=${keyword}`}
                  key={index}
                  onClick={() => setSearchModalOpen(false)}
                  aria-label="Search keyword"
                >
                  <span className="text-[#606060] font-[600] capitalize">{keyword}</span>
                </Link>
              );
            })}
          </div>
        )} */}
      </div>

      {/* NEWS SLIDER */}
      {!isLoading && data?.news?.docs?.length ? (
        <div className="relative mx-auto mt-8 mb-8 w-full max-w-[1421px] overflow-hidden px-10 sm:px-12 lg:px-14 sm:mt-10">
          <style jsx global>{`
            .search-modal-carousel .swiper-wrapper {
              align-items: stretch;
            }
            .search-modal-carousel .swiper-slide {
              height: auto;
              display: flex;
              width: 212px;
            }
            @media (min-width: 640px) {
              .search-modal-carousel .swiper-slide {
                width: 220px;
              }
            }
            @media (min-width: 1024px) {
              .search-modal-carousel .swiper-slide {
                width: 228px;
              }
            }
            .search-modal-carousel .swiper-slide > article {
              width: 100%;
              height: 100%;
            }
            .search-modal-card__title {
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              overflow: hidden;
              word-break: break-word;
            }
          `}</style>
          {data.news.docs.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => swiperRef.current?.slidePrev()}
                className="absolute left-0 top-[134px] z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-md hover:bg-gray-50 dark:bg-gray-800 dark:text-white sm:left-1"
                aria-label="Previous slide"
              >
                <FaChevronLeft size={14} />
              </button>
              <button
                type="button"
                onClick={() => swiperRef.current?.slideNext()}
                className="absolute right-0 top-[134px] z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-md hover:bg-gray-50 dark:bg-gray-800 dark:text-white sm:right-1"
                aria-label="Next slide"
              >
                <FaChevronRight size={14} />
              </button>
            </>
          )}
          <Swiper
            className="search-modal-carousel overflow-hidden"
            modules={[Autoplay, Mousewheel]}
            loop={(data?.news?.docs?.length ?? 0) > 4}
            loopAdditionalSlides={2}
            speed={400}
            slidesPerView="auto"
            slidesPerGroup={1}
            spaceBetween={12}
            grabCursor
            watchOverflow
            mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            onSwiper={swiper => (swiperRef.current = swiper)}
            onRealIndexChange={swiper => setCurrentSlide(swiper.realIndex)}
            onSlideChange={swiper => setCurrentSlide(swiper.realIndex)}
          >
            {data.news.docs.map((article, index) => {
              const { image, slug, title, categoryId, subCategoryId } = article;
              const articleUrl = getNewsDetailsUrl(categoryId, subCategoryId, slug, locale);
              const moreNewsUrl = getCategoryListUrlFromArticle(article, locale, true) || `/${locale}`;

              return (
                <SwiperSlide key={index}>
                  <article className="search-modal-card flex h-[268px] max-h-[268px] w-full flex-col overflow-hidden rounded-lg bg-[#f1f1f1] shadow-sm dark:bg-black">
                    <div className="relative h-[96px] w-full shrink-0 overflow-hidden bg-[#e8e8e8] dark:bg-gray-800">
                      <Link
                        href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
                        aria-label={`View news: ${title}`}
                        onClick={() => setSearchModalOpen(false)}
                        className="block h-full w-full"
                      >
                        <CustomImage
                          src={getArticleImageUrl(image, "sd")}
                          srcSet={getMediaSrcSet(image, ["ld", "sd"])}
                          sizes="200px"
                          alt={image?.alt || ""}
                          className="h-full w-full object-cover object-center img-zoom-hover"
                        />
                      </Link>
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pt-2">
                      <Link
                        href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
                        aria-label={`View news: ${title}`}
                        onClick={() => setSearchModalOpen(false)}
                        className="search-modal-card__title shrink-0 text-sm font-semibold leading-snug text-[#36454F] hover:opacity-90 dark:text-white"
                      >
                        {title}
                      </Link>
                    </div>

                    <div className="flex h-[44px] shrink-0 items-center justify-between gap-1.5 border-t border-gray-200 px-3 dark:border-gray-700">
                      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden uppercase">
                        <Link
                          href={`/${locale}/${categoryId?.slug}`}
                          aria-label={`View all news in the category: ${
                            getLocalizedString(categoryId?.name, locale) || categoryId?.slug
                          }`}
                          onClick={() => setSearchModalOpen(false)}
                          className="min-w-0 max-w-[45%] truncate text-[10px] font-medium text-[#36454F] dark:text-white"
                        >
                          {getLocalizedString(categoryId?.name, locale) || categoryId?.slug}
                        </Link>
                        {subCategoryId?.slug ? (
                          <>
                            <span className="shrink-0 text-[10px] text-gray-400">|</span>
                            <Link
                              href={`/${locale}/${categoryId?.slug}/${subCategoryId.slug}`}
                              aria-label={`View all news in the sub-category: ${
                                getLocalizedString(subCategoryId?.name, locale) || subCategoryId.slug
                              }`}
                              onClick={() => setSearchModalOpen(false)}
                              className="min-w-0 flex-1 truncate text-[10px] text-secondaryText dark:text-darkBgSecondaryColor"
                            >
                              {getLocalizedString(subCategoryId?.name, locale) || subCategoryId.slug}
                            </Link>
                          </>
                        ) : null}
                      </div>
                      <div className="flex h-7 shrink-0 items-center gap-1">
                        <SocialShare
                          title={title}
                          url={articleUrl}
                          dropdownPosition="down"
                          usePortal
                          customButton={
                            <button
                              type="button"
                              className="flex items-center justify-center p-0.5 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                              aria-label="Share"
                            >
                              <IoMdShare size={14} />
                            </button>
                          }
                        />
                        <NewsMoreOption
                          url={articleUrl}
                          moreUrl={moreNewsUrl}
                          dropdownPosition="down"
                          usePortal
                        />
                      </div>
                    </div>
                  </article>
                </SwiperSlide>
              );
            })}
          </Swiper>

          {data.news.docs.length > 1 && (
            <div className="mt-4 flex min-h-5 items-center justify-center gap-2 pb-1">
              {data.news.docs.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    const swiper = swiperRef.current;
                    if (!swiper) return;
                    if (swiper.params.loop && (data?.news?.docs?.length ?? 0) > 4) {
                      swiper.slideToLoop(index);
                    } else {
                      swiper.slideTo(index);
                    }
                    setCurrentSlide(index);
                  }}
                  className={`h-2.5 w-2.5 cursor-pointer rounded-full transition-all duration-300 hover:opacity-80 ${
                    currentSlide === index
                      ? "scale-110 bg-blue-500 dark:bg-blue-400"
                      : "bg-gray-300 dark:bg-gray-700"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
